import AddAlert from "@mui/icons-material/AddAlert";
import { Button, Link, useEventCallback } from "@mui/material";
import { GridColumnVisibilityModel, GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import {
  getNotificationCategoryLabel,
  getNotificationProgramAreaLabel,
} from "components/Notifications/notificationPresentation";
import {
  NotificationCategory,
  NotificationsQuery,
  NotificationsQueryVariables,
  ProgramArea,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { GET_NOTIFICATIONS } from "hooks/useNotifications";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import { NotificationDetailDrawer } from "./NotificationDetailDrawer";
import { QueueNotificationDialog } from "./QueueNotificationDialog";

type NotificationItem = NotificationsQuery["notifications"]["items"][number];
type DeliveryStats = NotificationsQuery["notifications"]["deliveryStats"][number];
type NotificationRow = NotificationItem & { deliveryStats: DeliveryStats | null };

const notificationProgramAreas = Object.values(ProgramArea);
const notificationCategories = Object.values(NotificationCategory);

const EXCLUDE_FILTER_COLUMNS = [
  "subject",
  "receivedCount",
  "readCount",
  "emailedCount",
  "emailFailedCount",
];
// A yes/no filter rather than one on the count column: "No" finds the
// notifications that reached nobody in-app.
const ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  { header: "Received in-app", key: "receivedInApp", filterKeyType: "boolean" },
];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "createdAt", sort: "desc" }];
// The message is a copy of the title for nearly every notification.
const INITIAL_COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  message: false,
  emailFailedCount: false,
};

function getItems(data: NotificationsQuery): NotificationRow[] {
  const statsById = new Map(
    data.notifications.deliveryStats.map((stats) => [stats.notificationId, stats])
  );
  return data.notifications.items.map((item) => ({
    ...item,
    deliveryStats: statsById.get(item.id) ?? null,
  }));
}

function getCount(data: NotificationsQuery): number {
  return data.notifications.count;
}

function buildVariables(
  search: string,
  pagination: { page: number; pageSize: number },
  sort: GridSortModel
): NotificationsQueryVariables {
  return {
    search,
    page: pagination.page,
    pageSize: pagination.pageSize,
    sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
  };
}

const COLUMNS: OmnisearchGridColDef<NotificationRow>[] = [
  {
    field: "createdAt",
    headerName: "Sent",
    width: 170,
    filterKeyType: "date",
    renderCell({ row }) {
      return prettyPrintDateTime(row.createdAt);
    },
  },
  {
    field: "tenant",
    headerName: "Organization",
    minWidth: 110,
    flex: 0.5,
    filterKeyType: "multipleTenant",
    valueGetter(_, row) {
      return row.tenant?.name;
    },
  },
  {
    field: "title",
    headerName: "Title",
    flex: 0.8,
  },
  {
    field: "message",
    headerName: "Message",
    flex: 1,
  },
  {
    field: "facility",
    headerName: "Facility",
    flex: 0.5,
    filterKeyType: "facility",
    valueGetter(_, row) {
      return row.facility?.name;
    },
  },
  {
    field: "programArea",
    headerName: "Program",
    minWidth: 100,
    flex: 0.3,
    filterKeyType: "enumAutocomplete",
    enumValues: notificationProgramAreas,
    enumPresentationFunction: (value) => getNotificationProgramAreaLabel(value),
    valueGetter(_, row) {
      return row.programArea ? getNotificationProgramAreaLabel(row.programArea) : "";
    },
  },
  {
    field: "category",
    headerName: "Category",
    minWidth: 100,
    flex: 0.3,
    filterKeyType: "enum",
    enumValues: notificationCategories,
    enumPresentationFunction: (value) => getNotificationCategoryLabel(value),
    valueGetter(_, row) {
      return getNotificationCategoryLabel(row.category);
    },
  },
  {
    field: "subject",
    headerName: "Linked record",
    minWidth: 110,
    flex: 0.5,
    sortable: false,
    valueGetter(_, row) {
      return row.subject?.label;
    },
    renderCell({ row }) {
      const subject = row.subject;
      if (!subject) {
        return null;
      }
      return subject.route ? (
        <Link
          component={RouterLink}
          to={subject.route}
          onClick={(event) => event.stopPropagation()}
        >
          {subject.label}
        </Link>
      ) : (
        subject.label
      );
    },
  },
  {
    field: "receivedCount",
    headerName: "Received (in-app)",
    type: "number",
    minWidth: 150,
    flex: 0.3,
    sortable: false,
    valueGetter(_, row) {
      return row.deliveryStats?.receivedCount;
    },
  },
  {
    field: "readCount",
    headerName: "Read",
    type: "number",
    flex: 0.25,
    sortable: false,
    valueGetter(_, row) {
      return row.deliveryStats?.readCount;
    },
  },
  {
    field: "emailedCount",
    headerName: "Emailed",
    type: "number",
    minWidth: 100,
    flex: 0.25,
    sortable: false,
    valueGetter(_, row) {
      return row.deliveryStats?.emailedCount;
    },
  },
  {
    field: "emailFailedCount",
    headerName: "Email failed",
    type: "number",
    minWidth: 120,
    flex: 0.3,
    sortable: false,
    valueGetter(_, row) {
      return row.deliveryStats?.emailFailedCount;
    },
  },
];

export const Notifications = () => {
  useBreadcrumb({
    label: "Notifications",
  });
  const [queueDialogOpen, setQueueDialogOpen] = useState(false);
  const {
    selectedId: notificationId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("notificationId");
  // The hook's callbacks change with every URL change; stable ones keep the memoized grid
  // from re-rendering every row when the drawer opens or closes.
  const openDetail = useEventCallback((params: GridRowParams<NotificationRow>) =>
    pushIdToUrl(String(params.id))
  );
  const closeDetail = useEventCallback(popIdFromUrl);
  const commandButtons = useMemo(
    () => [
      <Button
        key="queue-notification"
        variant="outlined"
        startIcon={<AddAlert />}
        onClick={() => setQueueDialogOpen(true)}
      >
        Queue notification
      </Button>,
    ],
    []
  );

  return (
    <>
      <OmnisearchDataGridV2
        query={GET_NOTIFICATIONS}
        getItems={getItems}
        getCount={getCount}
        buildVariables={buildVariables}
        columns={COLUMNS}
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
        initialSortModel={INITIAL_SORT_MODEL}
        initialColumnVisibilityModel={INITIAL_COLUMN_VISIBILITY}
        persistenceKey="staffNotifications"
        commandButtons={commandButtons}
        onRowClick={openDetail}
      />
      <NotificationDetailDrawer notificationId={notificationId ?? null} onClose={closeDetail} />
      {queueDialogOpen ? (
        <QueueNotificationDialog onClose={() => setQueueDialogOpen(false)} />
      ) : null}
    </>
  );
};
